import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'
import { generateProfilePictureSVG, generateGradientFromUsername } from '../utils/profilePicture'
import { sanitizeInput } from '../utils/validation'
import { checkForFlaggedWords, logFlaggedContent } from '../utils/flaggedWords'
import './Forum.css'

interface Comment {
  id: string
  postId: string
  content: string
  authorId: string
  authorUsername: string
  authorIsAdmin?: boolean
  createdAt: string
  likes: string[]
}

interface Post {
  id: string
  title: string
  content: string
  authorId: string
  authorUsername: string
  authorIsAdmin?: boolean
  createdAt: string
  updatedAt?: string
  likes: string[]
  comments: Comment[]
  isFeatured?: boolean
}

const Forum = () => {
  const { user } = useAuth()
  const { addNotification } = useNotifications()
  const [posts, setPosts] = useState<Post[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [error, setError] = useState('')
  const [commentingOn, setCommentingOn] = useState<string | null>(null)
  const [commentText, setCommentText] = useState('')
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)

  // Fetch posts from Supabase
  const fetchPosts = useCallback(async () => {
    const { data: postsData, error: postsError } = await supabase
      .from('forum_posts')
      .select('*, profiles!forum_posts_author_id_fkey(username, is_admin)')
      .order('created_at', { ascending: false })

    if (postsError) {
      console.error('Error fetching posts:', postsError)
      setLoading(false)
      return
    }

    // Fetch all comments
    const { data: commentsData } = await supabase
      .from('forum_comments')
      .select('*, profiles!forum_comments_author_id_fkey(username, is_admin)')
      .order('created_at', { ascending: false })

    // Fetch all post likes
    const { data: postLikes } = await supabase
      .from('forum_post_likes')
      .select('post_id, user_id')

    // Fetch all comment likes
    const { data: commentLikes } = await supabase
      .from('forum_comment_likes')
      .select('comment_id, user_id')

    const mappedPosts: Post[] = (postsData || []).map((post: Record<string, unknown>) => {
      const profile = post.profiles as Record<string, unknown> | null
      const postComments = (commentsData || [])
        .filter((c: Record<string, unknown>) => c.post_id === post.id)
        .map((c: Record<string, unknown>) => {
          const commentProfile = c.profiles as Record<string, unknown> | null
          const cLikes = (commentLikes || [])
            .filter((l: Record<string, unknown>) => l.comment_id === c.id)
            .map((l: Record<string, unknown>) => l.user_id as string)
          return {
            id: c.id as string,
            postId: c.post_id as string,
            content: c.content as string,
            authorId: c.author_id as string,
            authorUsername: commentProfile?.username as string || 'unknown',
            authorIsAdmin: commentProfile?.is_admin as boolean || false,
            createdAt: c.created_at as string,
            likes: cLikes,
          }
        })

      const pLikes = (postLikes || [])
        .filter((l: Record<string, unknown>) => l.post_id === post.id)
        .map((l: Record<string, unknown>) => l.user_id as string)

      return {
        id: post.id as string,
        title: post.title as string,
        content: post.content as string,
        authorId: post.author_id as string,
        authorUsername: profile?.username as string || 'unknown',
        authorIsAdmin: profile?.is_admin as boolean || false,
        createdAt: post.created_at as string,
        updatedAt: post.updated_at as string,
        likes: pLikes,
        comments: postComments,
        isFeatured: post.is_featured as boolean || false,
      }
    })

    setPosts(mappedPosts)
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  const handleCreate = () => {
    if (!user) return
    setIsCreating(true)
    setTitle('')
    setContent('')
    setError('')
  }

  const handleEdit = (post: Post) => {
    if (!user || (post.authorId !== user.id && !user.isAdmin)) return
    setEditingId(post.id)
    setTitle(post.title)
    setContent(post.content)
    setError('')
  }

  const handleCancel = () => {
    setIsCreating(false)
    setEditingId(null)
    setTitle('')
    setContent('')
    setError('')
    setCommentingOn(null)
    setCommentText('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    const sanitizedTitle = sanitizeInput(title)
    const sanitizedContent = sanitizeInput(content)

    if (!sanitizedTitle.trim() || !sanitizedContent.trim()) {
      setError('Title and content are required')
      return
    }

    if (sanitizedTitle.length > 200) {
      setError('Title must be less than 200 characters')
      return
    }

    if (sanitizedContent.length > 5000) {
      setError('Content must be less than 5000 characters')
      return
    }

    // Check for flagged words
    const titleCheck = checkForFlaggedWords(sanitizedTitle)
    const contentCheck = checkForFlaggedWords(sanitizedContent)

    if (titleCheck.hasFlagged || contentCheck.hasFlagged) {
      const flaggedWords = [...titleCheck.flaggedWords, ...contentCheck.flaggedWords]
      logFlaggedContent(
        `pending_${Date.now()}`,
        `${sanitizedTitle}\n\n${sanitizedContent}`,
        user.id,
        user.username,
        flaggedWords
      )
      setError(`Your content contains flagged words: ${flaggedWords.join(', ')}. It has been flagged for review.`)
      return
    }

    if (editingId) {
      const { error: updateError } = await supabase
        .from('forum_posts')
        .update({
          title: sanitizedTitle,
          content: sanitizedContent,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingId)

      if (updateError) {
        setError('Failed to update post')
        return
      }
      setEditingId(null)
    } else {
      const { error: insertError } = await supabase
        .from('forum_posts')
        .insert({
          author_id: user.id,
          title: sanitizedTitle,
          content: sanitizedContent,
        })

      if (insertError) {
        setError('Failed to create post')
        return
      }
      setIsCreating(false)
    }

    setTitle('')
    setContent('')
    setError('')
    await fetchPosts()
  }

  const handleDelete = async (postId: string) => {
    if (!user) return
    const post = posts.find(p => p.id === postId)
    if (!post || (post.authorId !== user.id && !user.isAdmin)) return

    if (window.confirm('Are you sure you want to delete this post?')) {
      const { error: deleteError } = await supabase
        .from('forum_posts')
        .delete()
        .eq('id', postId)

      if (deleteError) {
        console.error('Failed to delete post:', deleteError)
        return
      }

      // Optimistic update
      setPosts(prev => prev.filter(p => p.id !== postId))

      if (user.isAdmin && post.authorId !== user.id) {
        addNotification({
          type: 'content_removed',
          title: 'Content Removed',
          message: `Your post "${post.title.substring(0, 50)}..." was removed by an admin.`,
          userId: post.authorId,
        })
      }
    }
  }

  const handleToggleLike = async (postId: string) => {
    if (!user) return

    const post = posts.find(p => p.id === postId)
    if (!post) return

    const hasLiked = post.likes.includes(user.id)

    // Optimistic update — immediately reflect in UI
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const newLikes = hasLiked
          ? p.likes.filter(id => id !== user.id)
          : [...p.likes, user.id]
        return { ...p, likes: newLikes }
      }
      return p
    }))

    if (hasLiked) {
      await supabase
        .from('forum_post_likes')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', user.id)
    } else {
      await supabase
        .from('forum_post_likes')
        .insert({ post_id: postId, user_id: user.id })

      if (post.authorId !== user.id) {
        addNotification({
          type: 'like',
          title: 'New Like',
          message: `${user.username} liked your post "${post.title.substring(0, 50)}..."`,
          link: `/community?tab=forum&post=${postId}`,
          userId: post.authorId,
        })
      }
    }
  }

  const handleCommentSubmit = async (postId: string) => {
    if (!user || !commentText.trim()) return

    const sanitizedComment = sanitizeInput(commentText)
    const commentCheck = checkForFlaggedWords(sanitizedComment)

    if (commentCheck.hasFlagged) {
      logFlaggedContent(
        `comment_${Date.now()}`,
        sanitizedComment,
        user.id,
        user.username,
        commentCheck.flaggedWords
      )
      setError(`Your comment contains flagged words: ${commentCheck.flaggedWords.join(', ')}. It has been flagged for review.`)
      return
    }

    const { error: insertError } = await supabase
      .from('forum_comments')
      .insert({
        post_id: postId,
        author_id: user.id,
        content: sanitizedComment,
      })

    if (insertError) {
      setError('Failed to post comment')
      return
    }

    const post = posts.find(p => p.id === postId)
    if (post && post.authorId !== user.id) {
      addNotification({
        type: 'comment',
        title: 'New Comment',
        message: `${user.username} commented on your post "${post.title.substring(0, 50)}..."`,
        link: `/community?tab=forum&post=${postId}`,
        userId: post.authorId,
      })
    }

    setCommentingOn(null)
    setCommentText('')
    setError('')
    await fetchPosts()
  }

  const handleToggleCommentLike = async (postId: string, commentId: string) => {
    if (!user) return

    const post = posts.find(p => p.id === postId)
    const comment = post?.comments.find(c => c.id === commentId)
    if (!comment) return

    const hasLiked = comment.likes.includes(user.id)

    // Optimistic update
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const updatedComments = p.comments.map(c => {
          if (c.id === commentId) {
            return {
              ...c,
              likes: hasLiked
                ? c.likes.filter(id => id !== user.id)
                : [...c.likes, user.id],
            }
          }
          return c
        })
        return { ...p, comments: updatedComments }
      }
      return p
    }))

    if (hasLiked) {
      await supabase
        .from('forum_comment_likes')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.id)
    } else {
      await supabase
        .from('forum_comment_likes')
        .insert({ comment_id: commentId, user_id: user.id })
    }
  }

  const handleDeleteComment = async (postId: string, commentId: string) => {
    if (!user) return
    const post = posts.find(p => p.id === postId)
    const comment = post?.comments.find(c => c.id === commentId)
    if (!comment || (comment.authorId !== user.id && !user.isAdmin)) return

    if (window.confirm('Are you sure you want to delete this comment?')) {
      const { error: deleteError } = await supabase
        .from('forum_comments')
        .delete()
        .eq('id', commentId)

      if (deleteError) {
        console.error('Failed to delete comment:', deleteError)
        return
      }

      // Optimistic update
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return { ...p, comments: p.comments.filter(c => c.id !== commentId) }
        }
        return p
      }))

      if (user.isAdmin && comment.authorId !== user.id) {
        addNotification({
          type: 'content_removed',
          title: 'Content Removed',
          message: 'Your comment was removed by an admin.',
          userId: comment.authorId,
        })
      }
    }
  }

  const toggleComments = (postId: string) => {
    const newExpanded = new Set(expandedComments)
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId)
    } else {
      newExpanded.add(postId)
    }
    setExpandedComments(newExpanded)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  // Filter and sort posts
  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      post.title.toLowerCase().includes(query) ||
      post.content.toLowerCase().includes(query) ||
      post.authorUsername.toLowerCase().includes(query) ||
      post.comments.some(comment =>
        comment.content.toLowerCase().includes(query) ||
        comment.authorUsername.toLowerCase().includes(query)
      )
    )
  })

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (a.isFeatured && !b.isFeatured) return -1
    if (!a.isFeatured && b.isFeatured) return 1
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  if (loading) {
    return (
      <div className="forum-container">
        <div className="forum-empty">
          <p>loading forum...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="forum-container">
      <div className="forum-header">
        <h2 className="forum-title">community forum</h2>
        {user && (
          <button className="forum-create-button" onClick={handleCreate}>
            <i className="fas fa-plus"></i>
            <span>new post</span>
          </button>
        )}
      </div>

      <div className="forum-search">
        <input
          type="text"
          className="forum-search-input"
          placeholder="search posts, comments, users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <i className="fas fa-search"></i>
      </div>

      {(isCreating || editingId) && (
        <form className="forum-post-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="forum-form-title"
            placeholder="post title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
          />
          <textarea
            className="forum-form-content"
            placeholder="write your post..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={5000}
            rows={8}
            required
          />
          {error && <div className="forum-error">{error}</div>}
          <div className="forum-form-actions">
            <button type="submit" className="forum-submit-button">
              {editingId ? 'update post' : 'create post'}
            </button>
            <button type="button" className="forum-cancel-button" onClick={handleCancel}>
              cancel
            </button>
          </div>
        </form>
      )}

      <div className="forum-posts">
        {sortedPosts.length === 0 ? (
          <div className="forum-empty">
            <p>no posts yet. be the first to share!</p>
          </div>
        ) : (
          sortedPosts.map((post) => (
            <div key={post.id} className={`forum-post ${post.isFeatured ? 'featured' : ''}`}>
              {post.isFeatured && (
                <div className="forum-featured-badge">
                  <i className="fas fa-star"></i>
                  <span>featured</span>
                </div>
              )}
              <div className="forum-post-header">
                <div className="forum-post-author">
                  <div
                    className="forum-author-avatar"
                    style={{ background: generateGradientFromUsername(post.authorUsername) }}
                    dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(post.authorUsername, 40) }}
                  />
                  <div className="forum-author-info">
                    <Link
                      to={`/user/${post.authorUsername}`}
                      className="forum-author-username"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {post.authorUsername}
                      {post.authorIsAdmin && <span className="forum-admin-badge">admin</span>}
                    </Link>
                    <div className="forum-post-date">
                      {formatDate(post.createdAt)}
                      {post.updatedAt && post.updatedAt !== post.createdAt && ' (edited)'}
                    </div>
                  </div>
                </div>
                {(user?.id === post.authorId || user?.isAdmin) && (
                  <div className="forum-post-actions">
                    {user?.id === post.authorId && (
                      <button
                        className="forum-action-button"
                        onClick={() => handleEdit(post)}
                        aria-label="Edit post"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    )}
                    <button
                      className="forum-action-button forum-action-delete"
                      onClick={() => handleDelete(post.id)}
                      aria-label="Delete post"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              <Link to={`/community?tab=forum&post=${post.id}`} className="forum-post-link">
                <h3 className="forum-post-title">{post.title}</h3>
                <div className="forum-post-content">{post.content}</div>
              </Link>

              <div className="forum-post-footer">
                <button
                  className={`forum-like-button ${post.likes.includes(user?.id || '') ? 'liked' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleToggleLike(post.id)
                  }}
                  disabled={!user}
                >
                  <i className="fas fa-heart"></i>
                  <span>{post.likes.length}</span>
                </button>
                <button
                  className="forum-comment-toggle"
                  onClick={() => toggleComments(post.id)}
                >
                  <i className="fas fa-comment"></i>
                  <span>{post.comments.length} {post.comments.length === 1 ? 'comment' : 'comments'}</span>
                </button>
              </div>

              {expandedComments.has(post.id) && (
                <div className="forum-comments-section">
                  {user && (
                    <div className="forum-comment-form">
                      <textarea
                        className="forum-comment-input"
                        placeholder="write a comment..."
                        value={commentingOn === post.id ? commentText : ''}
                        onChange={(e) => {
                          setCommentingOn(post.id)
                          setCommentText(e.target.value)
                        }}
                        rows={3}
                        maxLength={1000}
                      />
                      <button
                        className="forum-comment-submit"
                        onClick={() => handleCommentSubmit(post.id)}
                        disabled={!commentText.trim() || commentingOn !== post.id}
                      >
                        post comment
                      </button>
                    </div>
                  )}
                  <div className="forum-comments-list">
                    {post.comments.map((comment) => (
                      <div key={comment.id} className="forum-comment">
                        <div className="forum-comment-header">
                          <div className="forum-comment-author">
                            <div
                              className="forum-comment-avatar"
                              style={{ background: generateGradientFromUsername(comment.authorUsername) }}
                              dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(comment.authorUsername, 32) }}
                            />
                            <div className="forum-comment-author-info">
                              <span className="forum-comment-username">
                                {comment.authorUsername}
                                {comment.authorIsAdmin && <span className="forum-admin-badge-small">admin</span>}
                              </span>
                              <span className="forum-comment-date">{formatDate(comment.createdAt)}</span>
                            </div>
                          </div>
                          {(user?.id === comment.authorId || user?.isAdmin) && (
                            <button
                              className="forum-comment-delete"
                              onClick={() => handleDeleteComment(post.id, comment.id)}
                              aria-label="Delete comment"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                        <div className="forum-comment-content">{comment.content}</div>
                        <button
                          className={`forum-comment-like ${comment.likes.includes(user?.id || '') ? 'liked' : ''}`}
                          onClick={() => handleToggleCommentLike(post.id, comment.id)}
                          disabled={!user}
                        >
                          <i className="fas fa-heart"></i>
                          <span>{comment.likes.length}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default Forum
